/** FOUR intentionally distinct variants, NOT duplicates: IconWrapper (stroke outline), FilledIconWrapper (solid fill), ColorIconWrapper (threads color to children), CircleIconWrapper (+ circle base). */

import { SVG_NAMESPACE, SVG_VIEWBOX_24 } from '@federa/const/svg-attributes';
import { INHERITED_ICON_COLOR } from './semantic-tokens.ts';
import type { ReactNode, SVGAttributes } from 'react';
import type { IconWrapperProps } from '@federa/types/ui-icons';

/** Private shared SVG shell -- public wrappers configure paint attributes */
function SvgShell({
	size = 24,
	className,
	style,
	children,
	paintAttrs,
	...props
}: Omit<IconWrapperProps, 'color'> & {
	paintAttrs: SVGAttributes<SVGSVGElement>;
}) {
	return (
		<svg
			width={size}
			height={size}
			viewBox={SVG_VIEWBOX_24}
			className={className}
			style={style}
			xmlns={SVG_NAMESPACE}
			aria-hidden="true"
			{...paintAttrs}
			{...props}
		>
			{children}
		</svg>
	);
}

function createPaintWrapper(toPaintAttrs: (color: string) => SVGAttributes<SVGSVGElement>) {
	return function PaintWrapper({ color = INHERITED_ICON_COLOR, children, ...props }: IconWrapperProps) {
		return (
			<SvgShell paintAttrs={toPaintAttrs(color)} {...props}>
				{children}
			</SvgShell>
		);
	};
}

/** Stroke-based wrapper (fill="none", stroke={color}) - for outline icons */
export const IconWrapper = createPaintWrapper((color) => ({
	fill: 'none',
	stroke: color,
	strokeWidth: '2',
	strokeLinecap: 'round',
	strokeLinejoin: 'round',
}));

/** Filled icon wrapper for solid icon variants: uses fill instead of stroke. */
export const FilledIconWrapper = createPaintWrapper((color) => ({
	fill: color,
	stroke: 'none',
}));

/** Color-threading icon wrapper: passes the resolved color to children via a render function, for icons needing fill and stroke on different children. */
export function ColorIconWrapper({
	color = INHERITED_ICON_COLOR,
	children,
	...props
}: Omit<IconWrapperProps, 'children'> & {
	children: (resolvedColor: string) => ReactNode;
}) {
	return (
		<SvgShell paintAttrs={{ fill: 'none' }} {...props}>
			{children(color)}
		</SvgShell>
	);
}

/** Circle icon wrapper: includes the standard circle (cx=12, cy=12, r=10) so components define only their interior elements. */
export function CircleIconWrapper(props: IconWrapperProps) {
	const { children, ...rest } = props;
	return (
		<IconWrapper {...rest}>
			<circle cx="12" cy="12" r="10" />
			{children}
		</IconWrapper>
	);
}
